import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { ThemeContext } from '../context/ThemeContext';
import { API_BASE } from '../api';

function AssignedTasks() {
  const [tasks, setTasks] = useState([]);
  const [editors, setEditors] = useState([]);
  const [designers, setDesigners] = useState([]); 
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [selectedTask, setSelectedTask] = useState(null);
  const { currentTheme } = useContext(ThemeContext);
  const navigate = useNavigate();


  // 🎨 GLOBAL COLORS DEFINITION
  const colors = {
    backgroundDark: '#0f0c29', 
    backgroundMedium: '#302b63', 
    backgroundLight: '#24243e',
    textLight: 'rgba(255, 255, 255, 0.8)',
    borderDark: 'rgba(255, 255, 255, 0.2)',

    primary: currentTheme?.primary || '#6366F1',
    secondary: currentTheme?.secondary || '#8B5CF6',
    accent: currentTheme?.accent || '#06B6D4',
    success: '#10B981',
    warning: '#F59E0B',
    danger: '#EF4444',

    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    gradient2: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', 
    gradient3: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', 
    gradient4: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' 
  };

  // ✅ VIDEO TYPE CONFIGURATION
  const videoTypes = [
    { value: 'indoor_reel', label: 'Indoor Reel', color: colors.primary, icon: 'fa-home' },
    { value: 'outdoor_reel', label: 'Outdoor Reel', color: colors.success, icon: 'fa-tree' },
    { value: 'podcast', label: 'Podcast', color: colors.secondary, icon: 'fa-podcast' },
    { value: 'indoor_long_video', label: 'Indoor Long', color: colors.warning, icon: 'fa-video' },
    { value: 'outdoor_long_video', label: 'Outdoor Long', color: colors.accent, icon: 'fa-mountain' },
  ];

  // ✅ VIDEO TYPE BADGE COMPONENT
  const getVideoTypeBadge = (videoType) => {
    if (!videoType || videoType === 'NULL' || videoType === 'null' || !videoTypes.find(type => type.value === videoType)) {
      return (
        <span 
          className="badge" 
          style={{
            background: '#6B7280',
            color: 'white',
            fontSize: '0.65rem',
            padding: '4px 8px',
            borderRadius: '6px',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            minWidth: '70px',
            justifyContent: 'center',
            border: '1px solid rgba(255,255,255,0.2)'
          }}
          title="Video type not specified"
        >
          <i className="fas fa-question" style={{fontSize: '0.5rem'}}></i>
          N/A
        </span>
      );
    }

    const typeConfig = videoTypes.find(type => type.value === videoType);
    
    return (
      <span 
        className="badge" 
        style={{
          background: typeConfig.color,
          color: 'white',
          fontSize: '0.65rem',
          padding: '4px 8px',
          borderRadius: '6px',
          fontWeight: '600',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          minWidth: '70px',
          justifyContent: 'center',
          border: '1px solid rgba(255,255,255,0.2)'
        }}
        title={typeConfig.label}
      >
        <i className={`fas ${typeConfig.icon}`} style={{fontSize: '0.5rem'}}></i>
        {typeConfig.label.split(' ')[0]}
      </span>
    );
  };

  // ✅ NOTES FUNCTIONALITY - Tooltip component
  const Tooltip = ({ text, children, maxWidth = '300px' }) => {
    const [showTooltip, setShowTooltip] = useState(false);

    return (
      <div 
        style={{
          position: 'relative',
          display: 'inline-block',
          width: '100%'
        }}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {children}
        {showTooltip && text && (
          <div style={{
            visibility: 'visible',
            width: maxWidth,
            backgroundColor: '#1a1a2e',
            color: 'white',
            textAlign: 'center',
            borderRadius: '8px',
            padding: '10px',
            position: 'absolute',
            zIndex: 1000,
            bottom: '125%',
            left: '50%',
            transform: 'translateX(-50%)',
            opacity: 1,
            transition: 'opacity 0.3s',
            fontSize: '0.75rem',
            border: `1px solid ${colors.borderDark}`,
            backdropFilter: 'blur(10px)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word'
          }}>
            {text}
          </div>
        )}
      </div>
    );
  };

  // ✅ NOTES FUNCTIONALITY - Truncate text
  const truncateText = (text, maxLength = 15) => {
    if (!text || text === '-' || text === 'NULL' || text === 'null') return 'N/A';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  useEffect(() => {
    fetchTasks();
    fetchEditors();
    fetchDesigners(); 
  }, []);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      setMessage('');
      
      const response = await fetch(`${API_BASE}/get_tasks.php`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        throw new Error(`Server returned HTML instead of JSON. Check your PHP API.`);
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      let tasksArray = [];
      if (Array.isArray(data)) {
        tasksArray = data;
      } else if (data && typeof data === 'object') {
        if (data.data && Array.isArray(data.data)) {
          tasksArray = data.data;
        } else if (data.success !== undefined) {
          throw new Error(data.message || 'Invalid response format from server');
        } else {
          tasksArray = [data];
        }
      }
      
      const assignedTasks = tasksArray.filter(task => task.editor_id && task.editor_id !== '');
      setTasks(assignedTasks);
      
    } catch (error) {
      console.error('Error fetching tasks:', error);
      setMessage('❌ Error loading tasks: ' + error.message);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchEditors = async () => {
    try {
      const response = await fetch(`${API_BASE}/get_editors.php`);
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        throw new Error(`Server returned HTML instead of JSON. Response: ${text.substring(0, 200)}`);
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (Array.isArray(data)) {
        setEditors(data);
      } else {
        setEditors([]);
      }
    } catch (error) {
      console.error('Error fetching editors:', error);
      setEditors([]);
    }
  };

  const fetchDesigners = async () => {
    try {
      const response = await fetch(`${API_BASE}/get_designers.php`);
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        throw new Error(`Server returned HTML instead of JSON. Response: ${text.substring(0, 200)}`);
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success && Array.isArray(data.data)) {
        setDesigners(data.data);
      } else if (Array.isArray(data)) {
        setDesigners(data);
      } else {
        setDesigners([]);
      }
    } catch (error) {
      console.error('❌ Error fetching designers:', error);
      setDesigners([]);
    }
  };

  const getEditorDisplayName = (task) => {
    if (task.editor_name && task.editor_name !== '' && task.editor_name !== 'Unassigned') {
      return task.editor_name;
    }
    
    if (task.editor && task.editor !== '' && task.editor !== 'Unassigned') {
      return task.editor;
    }
    
    if (task.editor_id && editors.length > 0) {
      const foundEditor = editors.find(editor => editor.id == task.editor_id);
      if (foundEditor && foundEditor.name) {
        return foundEditor.name;
      }
    }
    
    if (task.editor_id) {
      return `Editor #${task.editor_id}`;
    }
    
    return 'Unassigned';
  };

  const getDesignerDisplayName = (task) => {
    // First check for designer_name in task
    if (task.designer_name && task.designer_name !== '' && task.designer_name !== 'Unassigned' && task.designer_name !== 'N/A') {
      return task.designer_name;
    }
    
    // Check for designer_id and match with designers list
    if (task.designer_id && designers.length > 0) {
      const foundDesigner = designers.find(designer => designer.id == task.designer_id);
      if (foundDesigner && foundDesigner.name) {
        return foundDesigner.name;
      }
    }

    // Fallback to designer_id if available
    if (task.designer_id) {
      return `Designer #${task.designer_id}`;
    }
    
    return 'N/A';
  };

  const handleOpenLink = (link, type) => {
    if (link && link !== '-' && link !== 'NULL' && link !== 'null') {
      let formattedLink = link;
      if (!formattedLink.startsWith('http://') && !formattedLink.startsWith('https://')) {
        formattedLink = 'https://' + formattedLink;
      }
      window.open(formattedLink, '_blank', 'noopener,noreferrer');
    } else {
      setMessage(`❌ No ${type} link available for this task`);
    }
  };

  const handleVideoNameClick = (task) => {
    navigate('/task-full-details', { state: { task } });
  };

  const handleClientClick = (clientName, clientId) => {
    const clientTasks = tasks.filter(task => task.client_name === clientName);
    navigate('/client-tasks', { 
      state: { 
        clientId: clientId,
        clientName: clientName,
        tasks: clientTasks
      } 
    });
  };

  const handleOperatorClick = (operatorName, operatorId) => {
    const operatorTasks = tasks.filter(task => task.operator_id === operatorId);
    navigate('/operator-tasks', { 
      state: { 
        operatorId: operatorId,
        operatorName: operatorName,
        tasks: operatorTasks
      } 
    });
  };

  const handleEditorClick = (editorName, editorId = null) => {
    if (!editorName || editorName === 'Unassigned') return;
    
    const editorTasks = tasks.filter(task => {
      const taskEditorName = getEditorDisplayName(task);
      return taskEditorName === editorName && taskEditorName !== 'Unassigned';
    });
    
    if (editorTasks.length > 0) {
      navigate('/editor-tasks', { 
        state: { 
          editorId: editorId,
          editorName: editorName,
          tasks: editorTasks
        } 
      });
    } else {
      setMessage(`❌ No tasks found for editor ${editorName}`);
    }
  };

  // ✅ NEW FUNCTION: Handle designer click
  const handleDesignerClick = (designerName, designerId = null) => {
    if (!designerName || designerName === 'N/A' || designerName === 'Unassigned') return;
    
    // Filter tasks for this designer
    const designerTasks = tasks.filter(task => {
      const taskDesignerName = getDesignerDisplayName(task);
      return taskDesignerName === designerName && taskDesignerName !== 'N/A';
    });
    
    if (designerTasks.length > 0) {
      navigate('/designer-task', { 
        state: { 
          designerId: designerId,
          designerName: designerName,
          tasks: designerTasks
        } 
      });
    } else {
      setMessage(`❌ No tasks found for designer ${designerName}`);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { class: 'warning', text: 'Pending', icon: 'fa-clock' },
      in_progress: { class: 'info', text: 'In Progress', icon: 'fa-spinner' },
      completed: { class: 'success', text: 'Completed', icon: 'fa-check' },
      needs_revision: { class: 'danger', text: 'Needs Revision', icon: 'fa-exclamation-triangle' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`badge bg-${config.class}`} style={{fontSize: '0.7rem'}}>
        <i className={`fas ${config.icon} me-1`}></i>
        {config.text}
      </span>
    );
  };

  const getEditStatusBadge = (task) => {
    const editStatus = task.edited_status || 'pending';
    const statusConfig = {
      pending: { class: 'secondary', text: 'Not Started', icon: 'fa-clock' },
      in_progress: { class: 'info', text: 'Editing', icon: 'fa-edit' },
      review: { class: 'warning', text: 'In Review', icon: 'fa-eye' },
      completed: { class: 'success', text: 'Completed', icon: 'fa-check' },
      revisions: { class: 'warning', text: 'Revisions', icon: 'fa-sync' }
    };
    
    const config = statusConfig[editStatus] || statusConfig.pending;
    return (
      <span className={`badge bg-${config.class}`} style={{fontSize: '0.7rem'}}>
        <i className={`fas ${config.icon} me-1`}></i>
        {config.text}
      </span>
    );
  };

  const getLinkButton = (link, type, icon) => {
    if (link && link !== '-' && link !== 'NULL' && link !== 'null') {
      return (
        <button
          className="btn btn-sm"
          onClick={() => handleOpenLink(link, type)}
          title={`Open ${type}: ${link}`}
          style={{
            background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
            color: 'white',
            border: 'none',
            padding: '4px 8px',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '0.75rem',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = 'none';
          }}
        >
          <i className={`fas ${icon} me-1`}></i>
          View
        </button>
      );
    }
    
    return (
      <span style={{color: colors.textLight, fontSize: '0.75rem'}}>-</span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === 'NULL' || dateString === '0000-00-00') return 'N/A';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid Date';
    }
  };

  const handleRowClick = (task) => {
    setSelectedTask(selectedTask?.id === task.id ? null : task);
  };

  const isLinkAvailable = (link) => {
    return link && link !== '-' && link !== 'NULL' && link !== 'null' && link.trim() !== '';
  };

  // ✅ FIXED: Removed color: 'white' from tableRow to show blue data
  const styles = {
    container: {
      minHeight: '100vh',
      background: `linear-gradient(135deg, ${colors.backgroundDark} 0%, ${colors.backgroundMedium} 50%, ${colors.backgroundLight} 100%)`,
      position: 'relative',
      overflowX: 'hidden'
    },
    animatedBackground: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      pointerEvents: 'none'
    },
    floatingOrb: {
      position: 'absolute',
      borderRadius: '50%',
      filter: 'blur(40px)',
      opacity: 0.3,
      animation: 'float 8s ease-in-out infinite'
    },
    orb1: {
      width: '300px',
      height: '300px',
      background: colors.primary,
      top: '10%',
      left: '5%',
      animationDelay: '0s'
    },
    orb2: {
      width: '200px',
      height: '200px',
      background: colors.secondary,
      top: '60%',
      right: '10%',
      animationDelay: '2s'
    },
    orb3: {
      width: '250px',
      height: '250px',
      background: colors.accent,
      bottom: '20%',
      left: '15%',
      animationDelay: '4s'
    },
    orb4: {
      width: '180px',
      height: '180px',
      background: colors.success,
      top: '30%',
      right: '20%',
      animationDelay: '6s'
    },
    gridOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundImage: `linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
                         linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)`,
      backgroundSize: '50px 50px'
    },
    header: {
      padding: '30px 30px 20px',
      position: 'relative',
      zIndex: 2
    },
    headerContent: {
      maxWidth: '1400px',
      margin: '0 auto'
    },
    greeting: {
      display: 'flex',
      alignItems: 'center',
      gap: '20px',
      marginBottom: '15px'
    },
    pageTitle: {
      fontSize: '2.5rem',
      fontWeight: '800',
      background: 'linear-gradient(135deg, #fff 0%, #a5b4fc 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      margin: 0
    },
    welcomeBadge: {
      background: 'rgba(255, 255, 255, 0.1)',
      padding: '8px 16px',
      borderRadius: '20px',
      border: `1px solid ${colors.borderDark}`,
      backdropFilter: 'blur(10px)',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    badgeText: {
      color: 'white',
      fontWeight: '600',
      fontSize: '0.9rem'
    },
    badgeRole: {
      background: colors.primary,
      color: 'white',
      padding: '2px 8px',
      borderRadius: '12px',
      fontSize: '0.7rem',
      fontWeight: '600',
      textTransform: 'uppercase'
    },
    pageSubtitle: {
      color: colors.textLight,
      fontSize: '1rem',
      margin: 0,
      maxWidth: '600px'
    },
    messageAlert: {
      color: 'white',
      padding: '15px 20px',
      borderRadius: '12px',
      margin: '0 30px 20px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      maxWidth: '1400px',
      marginLeft: 'auto',
      marginRight: 'auto',
      fontSize: '0.9rem'
    },
    alertIcon: {
      fontSize: '1.2rem'
    },
    alertMessage: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      color: 'white'
    },
    alertClose: {
      background: 'none',
      border: 'none',
      color: colors.danger,
      cursor: 'pointer',
      padding: '6px',
      borderRadius: '6px',
      transition: 'background 0.3s ease',
      fontSize: '0.9rem'
    },
    content: {
      padding: '0 30px 30px',
      maxWidth: '1400px',
      margin: '0 auto',
      position: 'relative',
      zIndex: 2
    },
    formCard: {
      background: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '20px',
      border: `1px solid ${colors.borderDark}`,
      backdropFilter: 'blur(10px)',
      overflow: 'hidden',
      transition: 'all 0.3s ease',
      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.2)'
    },
    cardHeader: {
      background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
      color: 'white',
      padding: '20px 25px'
    },
    cardTitle: {
      fontSize: '1.5rem',
      fontWeight: '700',
      margin: 0
    },
    cardBody: {
      padding: '30px'
    },
    refreshBtn: {
      background: 'rgba(255, 255, 255, 0.1)',
      color: 'white',
      border: `1px solid ${colors.borderDark}`,
      padding: '10px 20px',
      borderRadius: '10px',
      fontWeight: '600',
      fontSize: '0.9rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      backdropFilter: 'blur(10px)',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    loadingState: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '80px 30px',
      color: 'white',
      textAlign: 'center'
    },
    loadingText: {
      fontSize: '1.8rem',
      margin: '0 0 15px 0',
      fontWeight: '600',
      background: 'linear-gradient(135deg, #fff 0%, #a5b4fc 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text'
    },
    loadingSubtext: {
      color: colors.textLight,
      margin: 0,
      fontSize: '1rem'
    },
    spinner: {
      width: '20px',
      height: '20px',
      border: '2px solid transparent',
      borderTop: '2px solid white',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    },
    tableHeader: {
      background: 'rgba(255, 255, 255, 0.1)',
      color: 'white',
      borderBottom: `2px solid ${colors.borderDark}`,
      padding: '12px 8px',
      fontWeight: '600',
      fontSize: '0.85rem',
      whiteSpace: 'nowrap'
    },
    // ✅ FIXED: Removed color: 'white' from tableRow - This allows blue data to show
    tableRow: {
      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
      transition: 'all 0.3s ease',
      '&:hover': {
        background: 'rgba(255, 255, 255, 0.03)'
      }
    },
    tableCell: {
      padding: '10px 8px',
      verticalAlign: 'middle',
      fontSize: '0.8rem',
      whiteSpace: 'nowrap'
    },
    statBadge: {
      background: 'rgba(255, 255, 255, 0.1)',
      color: 'white',
      padding: '12px 16px',
      borderRadius: '12px',
      border: `1px solid ${colors.borderDark}`,
      backdropFilter: 'blur(10px)',
      textAlign: 'center',
      minWidth: '100px'
    },
    statNumber: {
      display: 'block',
      fontSize: '1.5rem',
      fontWeight: 'bold',
      color: colors.primary,
      marginBottom: '4px'
    },
    statLabel: {
      fontSize: '0.8rem',
      color: colors.textLight
    },
    compactButton: {
      padding: '4px 8px',
      fontSize: '0.75rem',
      borderRadius: '6px',
      border: 'none',
      cursor: 'pointer',
      fontWeight: '600'
    },
    // New style for white text cells
    whiteTextCell: {
      padding: '10px 8px',
      verticalAlign: 'middle',
      fontSize: '0.8rem',
      whiteSpace: 'nowrap',
      color: 'white'
    }
  };

  if (loading) {
    return (
      <Layout>
        <div style={styles.container}>
          <div className="animated-background" style={styles.animatedBackground}>
            <div className="floating-orb orb-1" style={{...styles.floatingOrb, ...styles.orb1}}></div>
            <div className="floating-orb orb-2" style={{...styles.floatingOrb, ...styles.orb2}}></div>
            <div className="floating-orb orb-3" style={{...styles.floatingOrb, ...styles.orb3}}></div>
            <div className="floating-orb orb-4" style={{...styles.floatingOrb, ...styles.orb4}}></div>
            <div className="grid-overlay" style={styles.gridOverlay}></div>
          </div>
          <div style={styles.loadingState}>
            <div className="loading-spinner" style={styles.spinner}></div>
            <h3 style={styles.loadingText}>Loading Assigned Tasks</h3>
            <p style={styles.loadingSubtext}>Please wait while we fetch assigned task information</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={styles.container}>
        {/* Animated Background */}
        <div className="animated-background" style={styles.animatedBackground}>
          <div className="floating-orb orb-1" style={{...styles.floatingOrb, ...styles.orb1}}></div>
          <div className="floating-orb orb-2" style={{...styles.floatingOrb, ...styles.orb2}}></div>
          <div className="floating-orb orb-3" style={{...styles.floatingOrb, ...styles.orb3}}></div>
          <div className="floating-orb orb-4" style={{...styles.floatingOrb, ...styles.orb4}}></div>
          <div className="grid-overlay" style={styles.gridOverlay}></div>
        </div>

        {/* Header Section */}
        <div className="assigned-tasks-header" style={styles.header}>
          <div className="header-content" style={styles.headerContent}>
            <div className="welcome-section">
              <div className="greeting" style={styles.greeting}>
                <h1 className="page-title" style={styles.pageTitle}>
                  <i className="fas fa-user-check me-2"></i>
                  Assigned Tasks
                </h1>
                <div className="welcome-badge" style={styles.welcomeBadge}>
                  <span className="badge-text" style={styles.badgeText}>Task Management</span>
                  <span className="badge-role" style={styles.badgeRole}>Assigned</span>
                </div>
              </div>
              <p className="page-subtitle" style={styles.pageSubtitle}>
                Tasks that have been assigned to editors with detailed tracking information
              </p>
            </div>
            <div style={{marginTop: '15px', display: 'flex', alignItems: 'center', gap: '15px'}}>
              <div className="stat-badge" style={styles.statBadge}>
                <span className="stat-number" style={styles.statNumber}>{tasks.length}</span>
                <span className="stat-label" style={styles.statLabel}>Total Assigned</span>
              </div>
              <button 
                className="refresh-btn"
                onClick={fetchTasks}
                disabled={loading}
                style={styles.refreshBtn}
              >
                <i className={`fas fa-sync ${loading ? 'fa-spin' : ''} me-2`}></i>
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Message Alert */}
        {message && (
          <div className="message-alert" style={{
            ...styles.messageAlert,
            background: message.includes('✅') 
              ? 'rgba(16, 185, 129, 0.1)' 
              : 'rgba(220, 53, 69, 0.1)',
            border: message.includes('✅') 
              ? `1px solid ${colors.success}`
              : `1px solid ${colors.danger}`,
            color: message.includes('✅') 
              ? colors.success
              : colors.danger
          }}>
            <div className="alert-icon" style={{...styles.alertIcon, 
              color: message.includes('✅') 
                ? colors.success
                : colors.danger
            }}>
              <i className={`fas ${message.includes('✅') ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
            </div>
            <div className="alert-message" style={styles.alertMessage}>
              <strong style={{color: 'white'}}>{message.includes('✅') ? 'Success' : 'Error'}</strong>
              <span style={{color: 'white', fontSize: '0.85rem'}}>{message}</span>
            </div>
            <button className="alert-close" onClick={() => setMessage('')} style={styles.alertClose}>
              <i className="fas fa-times"></i>
            </button>
          </div>
        )}

        {/* Main Content */}
        <div className="assigned-tasks-content" style={styles.content}>
          <div className="tasks-card" style={styles.formCard}>
            <div className="card-header" style={styles.cardHeader}>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <h3 style={styles.cardTitle}>
                  <i className="fas fa-user-check me-2"></i>
                  Assigned Tasks Overview
                </h3>
                <small style={{color: 'white', fontSize: '0.9rem', fontWeight: '600'}}>
                  {tasks.length} assigned task{tasks.length !== 1 ? 's' : ''}
                </small>
              </div>
            </div>
            
            <div style={{padding: '0'}}>
              {tasks.length === 0 ? (
                <div style={{textAlign: 'center', padding: '50px 30px'}}>
                  <i className="fas fa-user-check fa-3x mb-3" style={{ color: `${colors.primary}20` }}></i>
                  <h5 style={{ color: 'white', marginBottom: '12px', fontSize: '1.2rem' }}>No Assigned Tasks</h5>
                  <p style={{ color: colors.textLight, marginBottom: '20px', fontSize: '0.9rem' }}>
                    All tasks are currently unassigned to editors.
                  </p>
                  <button 
                    className="btn"
                    onClick={fetchTasks}
                    style={{
                      ...styles.refreshBtn,
                      width: 'auto'
                    }}
                  >
                    <i className="fas fa-sync me-2"></i>
                    Try Again
                  </button>
                </div>
              ) : (
                <div style={{overflowX: 'auto'}}>
                  <table className="table table-hover mb-0" style={{width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem'}}>
                    <thead>
                      <tr>
                        {['#', 'Video', 'Type', 'Client', 'Operator', 'Editor', 'Designer', 'Raw', 'Ref', 'Edit Link', 'Status', 'Edit Status', 'Date', 'Notes'].map((header, index) => (
                          <th key={index} style={styles.tableHeader}>
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {tasks.map((task, index) => {
                        const editorName = getEditorDisplayName(task);
                        const designerName = getDesignerDisplayName(task);
                        const hasEditor = editorName && editorName !== 'Unassigned';
                        const hasDesigner = designerName && designerName !== 'N/A';
                        const notes = task.notes || task.editor_notes || '';
                        
                        return (
                          <React.Fragment key={task.id}>
                            <tr 
                              className={`task-row ${selectedTask?.id === task.id ? 'selected' : ''}`}
                              onClick={() => handleRowClick(task)}
                              style={{
                                ...styles.tableRow,
                                cursor: 'pointer',
                                background: selectedTask?.id === task.id ? 'rgba(255, 255, 255, 0.05)' : 'transparent'
                              }}
                            >
                              {/* Index - White */}
                              <td style={styles.whiteTextCell}>
                                <span style={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }}>
                                  {index + 1}
                                </span>
                              </td>
                              
                              {/* Video - White */}
                              <td style={styles.whiteTextCell}>
                                <Tooltip text={task.video_name}>
                                  <button 
                                    className="btn btn-link p-0 text-decoration-none text-start"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleVideoNameClick(task);
                                    }}
                                    style={{ 
                                      color: 'white', 
                                      fontWeight: '600', 
                                      background: 'none', 
                                      border: 'none', 
                                      cursor: 'pointer',
                                      fontSize: '0.75rem',
                                      textAlign: 'left'
                                    }}
                                  >
                                    {truncateText(task.video_name, 12)}
                                  </button>
                                </Tooltip>
                              </td>
                              
                              {/* Type - Keep badge styling */}
                              <td style={styles.tableCell}>
                                <Tooltip text={task.video_type ? `Video Type: ${task.video_type}` : 'Video type not specified'}>
                                  {getVideoTypeBadge(task.video_type)}
                                </Tooltip>
                              </td>
                              
                              {/* Client - White */}
                              <td style={styles.whiteTextCell}>
                                <Tooltip text={task.client_name}>
                                  <button 
                                    className="btn btn-link p-0 text-decoration-none"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleClientClick(task.client_name, task.client_id);
                                    }}
                                    style={{ 
                                      color: 'white', 
                                      fontWeight: '600', 
                                      background: 'none', 
                                      border: 'none', 
                                      cursor: 'pointer',
                                      fontSize: '0.75rem'
                                    }}
                                  >
                                    {truncateText(task.client_name, 10)}
                                  </button>
                                </Tooltip>
                              </td>
                              
                              {/* Operator - White */}
                              <td style={styles.whiteTextCell}>
                                <Tooltip text={task.operator_name}>
                                  <button 
                                    className="btn btn-link p-0 text-decoration-none"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleOperatorClick(task.operator_name, task.operator_id);
                                    }}
                                    style={{ 
                                      color: 'white', 
                                      fontWeight: '600', 
                                      background: 'none', 
                                      border: 'none', 
                                      cursor: 'pointer',
                                      fontSize: '0.75rem'
                                    }}
                                  >
                                    {truncateText(task.operator_name, 8)}
                                  </button>
                                </Tooltip>
                              </td>
                              
                              {/* Editor - White */}
                              <td style={styles.whiteTextCell}>
                                {hasEditor ? (
                                  <Tooltip text={editorName}>
                                    <button 
                                      className="btn btn-link p-0 text-decoration-none"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditorClick(editorName, task.editor_id);
                                      }}
                                      style={{ 
                                        color: 'white', 
                                        fontWeight: '600', 
                                        background: 'none', 
                                        border: 'none', 
                                        cursor: 'pointer',
                                        fontSize: '0.75rem'
                                      }}
                                    >
                                      <span style={{
                                        background: colors.primary, 
                                        color: 'white', 
                                        padding: '4px 8px', 
                                        borderRadius: '6px',
                                        fontSize: '0.7rem',
                                        fontWeight: '600'
                                      }}>
                                        <i className="fas fa-user-edit me-1"></i>
                                        {truncateText(editorName, 8)}
                                      </span>
                                    </button>
                                  </Tooltip>
                                ) : (
                                  <span style={{
                                    background: colors.secondary, 
                                    color: 'white', 
                                    padding: '4px 8px', 
                                    borderRadius: '6px',
                                    fontSize: '0.7rem',
                                    fontWeight: '600'
                                  }}>
                                    Unassigned
                                  </span>
                                )}
                              </td>
                              
                              {/* Designer - White with Clickable Link */}
                              <td style={styles.whiteTextCell}>
                                <Tooltip text={hasDesigner ? designerName : 'No Designer Assigned'}>
                                  {hasDesigner ? (
                                    <button 
                                      className="btn btn-link p-0 text-decoration-none"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDesignerClick(designerName, task.designer_id);
                                      }}
                                      style={{ 
                                        color: 'white', 
                                        fontWeight: '600', 
                                        background: 'none', 
                                        border: 'none', 
                                        cursor: 'pointer',
                                        fontSize: '0.75rem'
                                      }}
                                    >
                                      <span style={{
                                        background: colors.accent, 
                                        color: 'white', 
                                        padding: '4px 8px', 
                                        borderRadius: '6px',
                                        fontSize: '0.7rem',
                                        fontWeight: '600'
                                      }}>
                                        <i className="fas fa-palette me-1"></i>
                                        {truncateText(designerName, 8)}
                                      </span>
                                    </button>
                                  ) : (
                                    <span style={{ 
                                      color: 'white', 
                                      fontWeight: '600', 
                                      fontSize: '0.75rem' 
                                    }}>
                                      N/A
                                    </span>
                                  )}
                                </Tooltip>
                              </td>
                              
                              {/* Raw Link - Keep button styling */}
                              <td style={styles.tableCell}>
                                {getLinkButton(task.raw_link, 'raw link', 'fa-download')}
                              </td>
                              
                              {/* Reference Link - Keep button styling */}
                              <td style={styles.tableCell}>
                                {getLinkButton(task.reference_video_link, 'reference link', 'fa-eye')}
                              </td>
                              
                              {/* Edit Link - Keep button styling */}
                              <td style={styles.tableCell}>
                                {getLinkButton(task.editor_link, 'editor link', 'fa-user-edit')}
                              </td>
                              
                              {/* Status - Keep badge styling */}
                              <td style={styles.tableCell}>
                                {getStatusBadge(task.status)}
                              </td>
                              
                              {/* Edit Status - Keep badge styling */}
                              <td style={styles.tableCell}>
                                {getEditStatusBadge(task)}
                              </td>
                              
                              {/* Date - White */}
                              <td style={styles.whiteTextCell}>
                                <small style={{ color: 'white', fontSize: '0.75rem' }}>
                                  {formatDate(task.shoot_date || task.created_at)}
                                </small>
                              </td>
                              
                              {/* Notes - White */}
                              <td style={styles.whiteTextCell}>
                                <Tooltip text={notes || 'No notes available'}>
                                  <span style={{ 
                                    color: 'white', 
                                    cursor: notes ? 'help' : 'default',
                                    fontSize: '0.75rem'
                                  }}>
                                    {notes ? truncateText(notes, 12) : '-'}
                                  </span>
                                </Tooltip>
                              </td>
                            </tr>
                            
                            {/* Task Detail Panel */}
                            {selectedTask?.id === task.id && (
                              <tr className="task-detail-row">
                                <td colSpan="14" style={{ padding: '0', border: 'none' }}>
                                  <div style={{ 
                                    background: 'rgba(255, 255, 255, 0.02)', 
                                    padding: '20px',
                                    borderTop: `1px solid ${colors.borderDark}`,
                                    borderBottom: `1px solid ${colors.borderDark}`
                                  }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                      <h5 style={{ color: 'white', margin: 0, fontSize: '1.1rem' }}>
                                        <i className="fas fa-info-circle me-2"></i>
                                        Task Details: {task.video_name || 'Unnamed Task'}
                                      </h5>
                                      <button
                                        className="btn btn-sm"
                                        onClick={() => setSelectedTask(null)}
                                        style={{
                                          background: 'rgba(255, 255, 255, 0.1)',
                                          color: 'white',
                                          border: `1px solid ${colors.borderDark}`,
                                          padding: '6px 12px',
                                          borderRadius: '6px',
                                          cursor: 'pointer',
                                          fontSize: '0.8rem'
                                        }}
                                      >
                                        <i className="fas fa-times me-1"></i>
                                        Close
                                      </button>
                                    </div>
                                    
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '15px', marginBottom: '20px', fontSize: '0.85rem' }}>
                                      <div>
                                        <strong style={{ color: colors.textLight }}>Task ID:</strong>
                                        <div style={{ color: 'white' }}>#{task.id}</div>
                                      </div>
                                      <div>
                                        <strong style={{ color: colors.textLight }}>Client:</strong>
                                        <div style={{ color: 'white' }}>{task.client_name || 'N/A'}</div>
                                      </div>
                                      <div>
                                        <strong style={{ color: colors.textLight }}>Operator:</strong>
                                        <div style={{ color: 'white' }}>{task.operator_name || 'N/A'}</div>
                                      </div>
                                      <div>
                                        <strong style={{ color: colors.textLight }}>Editor:</strong>
                                        <div style={{ color: 'white' }}>{editorName}</div>
                                      </div>
                                      <div>
                                        <strong style={{ color: colors.textLight }}>Designer:</strong>
                                        <div style={{ color: 'white' }}>{designerName}</div>
                                      </div>
                                      <div>
                                        <strong style={{ color: colors.textLight }}>Video Type:</strong>
                                        <div style={{ color: 'white' }}>{task.video_type || 'N/A'}</div>
                                      </div>
                                      <div>
                                        <strong style={{ color: colors.textLight }}>Shoot Date:</strong>
                                        <div style={{ color: 'white' }}>{formatDate(task.shoot_date)}</div>
                                      </div>
                                      <div>
                                        <strong style={{ color: colors.textLight }}>Edit Status:</strong>
                                        <div style={{ color: 'white' }}>{task.edited_status || 'Not specified'}</div>
                                      </div>
                                      <div>
                                        <strong style={{ color: colors.textLight }}>Video Count:</strong>
                                        <div style={{ color: 'white' }}>{task.video_count || '0'}</div>
                                      </div>
                                    </div>

                                    {notes && (
                                      <div style={{ marginBottom: '15px' }}>
                                        <strong style={{ color: colors.textLight, marginBottom: '8px', display: 'block', fontSize: '0.9rem' }}>Notes:</strong>
                                        <div style={{ 
                                          background: 'rgba(255, 255, 255, 0.05)', 
                                          padding: '12px', 
                                          borderRadius: '8px',
                                          border: `1px solid ${colors.borderDark}`,
                                          color: 'white',
                                          fontSize: '0.85rem',
                                          whiteSpace: 'pre-wrap',
                                          wordBreak: 'break-word'
                                        }}>
                                          {notes}
                                        </div>
                                      </div>
                                    )}
                                    
                                    <div>
                                      <strong style={{ color: colors.textLight, marginBottom: '10px', display: 'block', fontSize: '0.9rem' }}>Quick Links:</strong>
                                      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                        {isLinkAvailable(task.raw_link) && (
                                          <button
                                            className="btn btn-sm"
                                            onClick={() => handleOpenLink(task.raw_link, 'raw link')}
                                            style={{
                                              background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                                              color: 'white',
                                              border: 'none',
                                              padding: '6px 12px',
                                              borderRadius: '6px',
                                              cursor: 'pointer',
                                              fontSize: '0.8rem'
                                            }}
                                          >
                                            <i className="fas fa-download me-1"></i>
                                            Raw Link
                                          </button>
                                        )}
                                        {isLinkAvailable(task.reference_video_link) && (
                                          <button
                                            className="btn btn-sm"
                                            onClick={() => handleOpenLink(task.reference_video_link, 'reference link')}
                                            style={{
                                              background: 'rgba(255, 255, 255, 0.1)',
                                              color: 'white',
                                              border: `1px solid ${colors.borderDark}`,
                                              padding: '6px 12px',
                                              borderRadius: '6px',
                                              cursor: 'pointer',
                                              fontSize: '0.8rem'
                                            }}
                                          >
                                            <i className="fas fa-eye me-1"></i>
                                            Reference Link
                                          </button>
                                        )}
                                        {isLinkAvailable(task.editor_link) && (
                                          <button
                                            className="btn btn-sm"
                                            onClick={() => handleOpenLink(task.editor_link, 'editor link')}
                                            style={{
                                              background: 'rgba(255, 255, 255, 0.1)',
                                              color: 'white',
                                              border: `1px solid ${colors.borderDark}`,
                                              padding: '6px 12px',
                                              borderRadius: '6px',
                                              cursor: 'pointer',
                                              fontSize: '0.8rem'
                                            }}
                                          >
                                            <i className="fas fa-user-edit me-1"></i>
                                            Editor Link
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .spinning {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @keyframes float {
          0%, 100% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }

        @keyframes wave {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }

        @keyframes bounce {
          0%, 80%, 100% {
            transform: scale(0);
          } 40% {
            transform: scale(1);
          }
        }
        
        .form-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 12px 25px rgba(0, 0, 0, 0.25);
        }
        .refresh-btn:hover:not(:disabled) {
          background: rgba(255, 255, 255, 0.2) !important;
          transform: translateY(-2px);
        }
        
        .table {
          --bs-table-bg: transparent;
        }

        .table-hover tbody tr:hover {
          background: rgba(255, 255, 255, 0.05) !important;
        }

        .task-row.selected {
          background: rgba(255, 255, 255, 0.08) !important;
        }
      `}</style>
    </Layout>
  );
}

export default AssignedTasks;